// Agent-side peer link. Mirror of packages/widget/src/webrtc/peer-link.ts
// but for the answerer role.
//
// The agent does NOT call `getUserMedia` until a real offer is on the bus —
// the canvas placeholder fills the tile in the meantime. Once an offer
// arrives we acquire the camera, return an answer, and stream remote tracks.

import type { AgentId, SessionId, VisitorId } from '@prakash-software/shared';
import { getBus } from '@prakash-software/shared';
import type { RTCSessionDescriptionLike } from '@prakash-software/shared';
import {
  acceptOffer,
  addLocalTracks,
  addRemoteIce,
  applyAnswer,
  closePeer,
  createPeer,
  makeOffer,
} from '@prakash-software/shared/webrtc';
import { takeBufferedOffer, clearBufferedOffer } from '../stores/webrtc-buffer';
import { cameraVideoConstraints } from './media-devices';

export interface AgentPeerLink {
  pc: RTCPeerConnection;
  stop: () => void;
}

export interface AgentLinkHandlers {
  sessionId: SessionId;
  agentId: AgentId;
  visitorId: VisitorId;
  onRemoteStream: (stream: MediaStream) => void;
  onConnected?: () => void;
  onIceFailed?: () => void;
  /** The visitor's peer vanished (reload/crash/drop) — end the call locally. */
  onPeerLost?: () => void;
  /** Fires once the PC + local stream are wired — Conversation route uses
   *  this to hook screen-share onto the active sender. */
  onReady?: (handle: { pc: RTCPeerConnection; localStream: MediaStream }) => void;
}

/**
 * Start watching for an inbound offer for the given session. As soon as one
 * arrives, acquire local camera + mic, accept the offer, send the answer,
 * and forward remote ICE. Returns a controller whose `stop()` tears down
 * the PC and unsubscribes from the bus.
 */
export function watchForOffer(h: AgentLinkHandlers): { stop: () => void } {
  const bus = getBus();
  let link: AgentPeerLink | null = null;
  let stopped = false;
  // IMPORTANT: ICE candidates from the visitor race ahead of the agent's
  // getUserMedia + createPeer + setRemoteDescription chain. Dropping them
  // (the old `if (!link) return` path) made the first call attempt
  // frequently never reach `connected`; the agent had to hang up and try
  // again. Buffer pre-link ICE here and flush after the peer is built.
  const pendingIce: import('@prakash-software/shared').RTCIceCandidateLike[] = [];

  // Drain any offer that arrived BEFORE we mounted (e.g. visitor sent it the
  // instant they saw the accept response). See stores/webrtc-buffer.ts.
  const buffered = takeBufferedOffer(h.sessionId);
  if (buffered) {
    void start(buffered).catch(() => {});
  }

  const offerUnsub = bus.on('webrtc:offer', (e) => {
    if (e.sessionId !== h.sessionId) return;
    if (link) return; // already negotiating
    void start(e.sdp).catch(() => {
      /* swallow — UI already shows an error if media acquisition fails */
    });
  });

  async function start(offer: RTCSessionDescriptionLike): Promise<void> {
    if (stopped) return;
    const local = await navigator.mediaDevices.getUserMedia({
      video: cameraVideoConstraints(),
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    if (stopped) {
      local.getTracks().forEach((t) => t.stop());
      return;
    }

    const pc = createPeer({
      onLocalIce: (candidate) => {
        bus.emit({
          type: 'webrtc:ice',
          sessionId: h.sessionId,
          from: h.agentId,
          to: h.visitorId,
          candidate,
        });
      },
      onRemoteTrack: h.onRemoteStream,
      onIceFailed: h.onIceFailed,
      onPeerLost: h.onPeerLost,
      onConnectionState: (s) => {
        if (s === 'connected') h.onConnected?.();
      },
    });
    addLocalTracks(pc, local);

    const answer = await acceptOffer(pc, offer);
    bus.emit({
      type: 'webrtc:answer',
      sessionId: h.sessionId,
      from: h.agentId,
      to: h.visitorId,
      sdp: answer,
    });

    link = {
      pc,
      stop: () => {
        local.getTracks().forEach((t) => t.stop());
        closePeer(pc);
      },
    };
    // Flush any ICE that came in while the async chain above was running.
    // Remote description is now set, so addRemoteIce will accept these.
    const drained = pendingIce.splice(0);
    for (const c of drained) {
      void addRemoteIce(pc, c).catch(() => {});
    }
    h.onReady?.({ pc, localStream: local });
  }

  const iceUnsub = bus.on('webrtc:ice', (e) => {
    if (e.sessionId !== h.sessionId) return;
    if (e.from === h.agentId) return;
    if (!link) {
      pendingIce.push(e.candidate);
      return;
    }
    void addRemoteIce(link.pc, e.candidate).catch(() => {});
  });

  return {
    stop: () => {
      stopped = true;
      offerUnsub();
      iceUnsub();
      pendingIce.length = 0;
      clearBufferedOffer(h.sessionId);
      if (link) {
        link.stop();
        link = null;
      }
    },
  };
}

/**
 * Open a peer link as the offerer (agent-initiated calls). Mirror of the
 * widget's `startVisitorOffer`. Acquires camera + mic, attaches tracks,
 * sends `webrtc:offer`, applies the inbound answer.
 */
export async function startAgentOffer(h: AgentLinkHandlers): Promise<AgentPeerLink> {
  const bus = getBus();
  const local = await navigator.mediaDevices.getUserMedia({
    video: cameraVideoConstraints(),
    audio: { echoCancellation: true, noiseSuppression: true },
  });

  const pc = createPeer({
    onLocalIce: (candidate) => {
      bus.emit({
        type: 'webrtc:ice',
        sessionId: h.sessionId,
        from: h.agentId,
        to: h.visitorId,
        candidate,
      });
    },
    onRemoteTrack: h.onRemoteStream,
    onIceFailed: h.onIceFailed,
    onPeerLost: h.onPeerLost,
    onConnectionState: (s) => {
      if (s === 'connected') h.onConnected?.();
    },
  });
  addLocalTracks(pc, local);

  // IMPORTANT: the visitor's ICE candidates routinely arrive before their
  // `webrtc:answer` (or before our async applyAnswer/setRemoteDescription
  // resolves). addIceCandidate before the remote description is set is
  // rejected, so adding them directly here silently DROPS them and the call
  // frequently never reaches `connected` ("ring goes, video doesn't"). Buffer
  // pre-answer ICE and flush once the answer is applied — the same fix the
  // answerer paths (watchForOffer / watchForOfferAsVisitor) already use.
  let remoteReady = false;
  const pendingIce: import('@prakash-software/shared').RTCIceCandidateLike[] = [];

  const unsubs: Array<() => void> = [];
  unsubs.push(
    bus.on('webrtc:answer', (e) => {
      if (e.sessionId !== h.sessionId) return;
      void applyAnswer(pc, e.sdp)
        .then(() => {
          remoteReady = true;
          const drained = pendingIce.splice(0);
          for (const c of drained) void addRemoteIce(pc, c).catch(() => {});
        })
        .catch(() => {});
    }),
  );
  unsubs.push(
    bus.on('webrtc:ice', (e) => {
      if (e.sessionId !== h.sessionId) return;
      if (e.from === h.agentId) return;
      if (!remoteReady) {
        pendingIce.push(e.candidate);
        return;
      }
      void addRemoteIce(pc, e.candidate).catch(() => {});
    }),
  );

  const offer = await makeOffer(pc);
  bus.emit({
    type: 'webrtc:offer',
    sessionId: h.sessionId,
    from: h.agentId,
    to: h.visitorId,
    sdp: offer,
  });

  h.onReady?.({ pc, localStream: local });

  return {
    pc,
    stop: () => {
      unsubs.forEach((u) => u());
      pendingIce.length = 0;
      local.getTracks().forEach((t) => t.stop());
      closePeer(pc);
    },
  };
}
